import { useState } from "react";

import { Loader2 } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type PersonSummary,
  usePeopleRepository,
} from "@/pages/people/hooks/use-people-repository";
import {
  asRecord,
  firstText,
  readTheaterPerformanceContext,
} from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { HitlResolutionInput } from "@/types/tina";

type Mode = "pick" | "tmdb" | "create";

const MODES: ReadonlyArray<{ mode: Mode; label: string }> = [
  { mode: "pick", label: "Use an existing person" },
  { mode: "tmdb", label: "Create from a TMDB link or ID" },
  { mode: "create", label: "Create a new person by name" },
];

/**
 * Tina's `PERSON_TARGET_DEPARTMENTS` resolution vocabulary — the only departments that get a
 * person-resolution hop at all (`src/pipeline/handlers/theater_performance/domain/
 * person_resolution.py`). Unlike a play credit's department (see `PLAY_DEPARTMENTS`, which
 * already lists all seven Tina distinguishes), widening this list needs Tina's target
 * vocabulary widened first — the resolution stage would reject any extra value.
 */
const PERSON_DEPARTMENTS = [
  "Acting",
  "Directing",
  "Writing",
  "Translating",
] as const;

export function PersonResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = usePeopleRepository();
  const ctx = readTheaterPerformanceContext(item, hitlContext);
  // Tina's `target.knownForDepartment` (camelCase, as sent on the wire by
  // `target_evidence` in `src/pipeline/handlers/theater_performance/domain/
  // person_resolution.py`) is the target's own known department;
  // `known_for_department` is tolerated as a snake_case alias but camelCase always
  // wins. A truthy default here (e.g. always "Acting") stamped every director and
  // playwright as an actor, so: prefill from the target when it says something,
  // otherwise start with no selection — the curator leaving it unset means "let the
  // resuming stage fall back to the target's known department" (a null `department`
  // on the wire).
  const target = asRecord(asRecord(hitlContext.problem).target);
  const knownForDepartment = firstText(
    target.knownForDepartment,
    target.known_for_department,
  );
  const [mode, setMode] = useState<Mode>("pick");
  const [picked, setPicked] = useState<PersonSummary | undefined>();
  const [tmdbRef, setTmdbRef] = useState("");
  const [name, setName] = useState(ctx?.targetPersonName ?? "");
  const [imageUrl, setImageUrl] = useState("");
  const [department, setDepartment] = useState<string>(
    knownForDepartment ?? "",
  );
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    mode === "pick"
      ? Boolean(picked?.id)
      : mode === "tmdb"
        ? tmdbRef.trim().length > 0
        : name.trim().length > 0;

  async function handleSubmit() {
    let resolution: HitlResolutionInput;
    if (mode === "pick") {
      resolution = { kind: "person.pick", person_id: picked!.id };
    } else if (mode === "tmdb") {
      resolution = {
        kind: "person.create_from_tmdb",
        tmdb_ref: tmdbRef.trim(),
      };
    } else {
      resolution = {
        kind: "person.create",
        name: name.trim(),
        image_url: imageUrl.trim() || null,
        department: department || null,
      };
    }
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {ctx?.targetPersonName && (
        <p className="text-sm text-muted-foreground">
          Resolving:{" "}
          <span className="font-medium text-foreground">
            {ctx.targetPersonName}
          </span>
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        {MODES.map(({ mode: m, label }) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? "default" : "outline"}
            onClick={() => setMode(m)}
          >
            {label}
          </Button>
        ))}
      </div>

      {mode === "pick" && (
        <RemoteSelect<PersonSummary>
          value={picked}
          triggerType="change"
          showSelected
          onValueChanged={(selected) => setPicked(selected)}
          accessorFn={(option) => ({
            id: option?.id || "",
            name: option ? option.name : "",
            iconUrl: option?.media?.photos?.[0]?.url,
          })}
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = await search(query);
            onStatusChanged("success");
            return results;
          }}
        />
      )}
      {mode === "tmdb" && (
        <div className="flex flex-col gap-1">
          <Input
            value={tmdbRef}
            onChange={(event) => setTmdbRef(event.target.value)}
            placeholder="https://www.themoviedb.org/person/… or a numeric TMDB id"
          />
          <span className="text-xs text-muted-foreground">
            Paste a themoviedb.org link or a numeric TMDB id. The person is
            created on resume if they aren't already in Mood.
          </span>
        </div>
      )}
      {mode === "create" && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="person-create-name">
              Name
            </label>
            <Input
              id="person-create-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Name as it appears on the bill"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-xs font-medium"
              htmlFor="person-create-image"
            >
              Image URL (optional)
            </label>
            <Input
              id="person-create-image"
              value={imageUrl}
              onChange={(event) => setImageUrl(event.target.value)}
              placeholder="https://… a portrait, or leave empty"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-xs font-medium"
              htmlFor="person-create-department"
            >
              Department
            </label>
            <select
              id="person-create-department"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={department}
              onChange={(event) => setDepartment(event.target.value)}
            >
              <option value="">
                Unspecified — use the target&apos;s known department
              </option>
              {PERSON_DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <span className="text-xs text-muted-foreground">
            Creates a new Mood person with no external id. Use this only when
            the searches above genuinely found nobody — the backend has no name
            dedup, so a second spelling becomes a second person.
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Resolve &amp; resume
        </Button>
        <span className="text-xs text-muted-foreground">
          Re-runs person_resolution with your choice and continues to create.
        </span>
      </div>
    </div>
  );
}
